import { Routes } from '@angular/router';
import { activeLobbyGuard } from '@core/online-play/guards/active-lobby.guard';

import { actionReplaysRoutes } from './modules/action-replays/action-replays.routes';
import { authenticationRoutes } from './modules/authentication/authentication.routes';
import { authGuard } from './modules/authentication/guards/auth.guard';
import { noRedirectAuthGuard } from './modules/authentication/guards/no-redirect-auth.guard';
import { notVenueModeGuard } from './modules/authentication/guards/not-venue-mode.guard';
import { unauthenticatedMatchGuard } from './modules/authentication/guards/unauthenticated-match.guard';
import { venueModeGuard } from './modules/authentication/guards/venue-mode.guard';
import { notBannedGuard } from './modules/banned/guards/not-banned.guard';
import { chatRoutes } from './modules/chats/chats.routes';
import { connectGuard } from './modules/connect/guards/connect.guard';
import { developmentGuard } from './modules/development/guards/development.guard';
import { gameRoutes } from './modules/games/games.routes';
import { spectatingOnlineGameRoutes } from './modules/games/spectating.routes';
import { localGameRoutes } from './modules/local-games/local-games.routes';
import { localTournamentPublicRoutes } from './modules/local-tournaments/local-tournaments.routes';
import { appMaintenanceGuard } from './modules/maintenance/guards/app-maintenance.guard';
import { serviceUnavailableGuard } from './modules/maintenance/guards/service-unavailable.guard';
import { onlineGamesRoutes } from './modules/online-games/online-games.routes';
import { profileRoutes } from './modules/profile/profile.routes';
import { rankingRoutes } from './modules/ranking/ranking.routes';
import { smartDeviceRoutes } from './modules/smart-devices/smart-devices.routes';
import { statisticsRoutes } from './modules/statistics/statistics.routes';
import { testingRoutes } from './modules/testing/testing.routes';
import { tournamentRoutes } from './modules/tournaments/tournaments.routes';
import { venuesRoutes } from './services/venues/venues.routes';
import { isWebGuard } from './shared/guards/is-web.guard';
import { DC_ROUTING, DcCommunityEventRoutes } from './shared/routing.tokens';

const appRoutes: Routes = [
    ...authenticationRoutes,

    {
        path: '',
        redirectTo: DC_ROUTING.DASHBOARD,
        pathMatch: 'full',
    },

    {
        path: '',
        canActivate: [noRedirectAuthGuard],
        loadComponent: () => import('./core/shell/layouts/shell/shell.component').then((c) => c.ShellComponent),
        children: [
            {
                path: DC_ROUTING.REDEEM_COUPON,
                loadComponent: () =>
                    import('./modules/redeem-coupon/pages/redeem-coupon/redeem-coupon.component').then(
                        (m) => m.RedeemCouponComponent
                    ),
            },
            {
                path: DC_ROUTING.VIRT,
                loadComponent: () =>
                    import('./modules/redeem-coupon/pages/virt-redeem-coupon/virt-redeem-coupon.component').then(
                        (m) => m.VirtRedeemCouponComponent
                    ),
            },
            {
                path: DC_ROUTING.OMNI,
                loadComponent: () =>
                    import('./modules/redeem-coupon/pages/omni-redeem-coupon/omni-redeem-coupon.component').then(
                        (m) => m.OmniRedeemCouponComponent
                    ),
            },
            {
                path: DC_ROUTING.CONTACT,
                loadComponent: () =>
                    import('./modules/contact/pages/contact/contact.component').then((c) => c.ContactComponent),
            },
            {
                path: DC_ROUTING.JOIN,
                loadComponent: () => import('./modules/join/pages/join/join.component').then((c) => c.JoinComponent),
            },
            {
                path: `${DC_ROUTING.JOIN}/:code`,
                loadComponent: () => import('./modules/join/pages/join/join.component').then((c) => c.JoinComponent),
            },
            {
                path: DC_ROUTING.UPGRADE,
                canActivate: [isWebGuard],
                loadComponent: () =>
                    import('./modules/upgrade/pages/upgrade/upgrade.component').then((c) => c.UpgradeComponent),
            },
            {
                path: `${DC_ROUTING.LINK}/:identityProvider`,
                loadComponent: () =>
                    import('./modules/authentication/pages/link/link.component').then((c) => c.LinkComponent),
            },

            // {
            //     path: DC_ROUTING.ENGAGE,
            //     loadComponent: () =>
            //         import('./modules/engage/pages/engage/engage.component').then((c) => c.EngageComponent),
            // },
            {
                path: DC_ROUTING.COMMUNITY_EVENTS,
                children: [
                    {
                        path: DcCommunityEventRoutes.HauntedOcheChallenge,
                        loadComponent: () =>
                            import('./modules/community-events/pages/haunted-oche-challenge/haunted-oche-challenge.component').then(
                                (c) => c.HauntedOcheChallengeComponent
                            ),
                    },
                ],
            },

            {
                path: 'profile/:id',
                canMatch: [unauthenticatedMatchGuard],
                canActivate: [notVenueModeGuard],
                data: {
                    hideTournamentNotification: true,
                },
                loadComponent: () =>
                    import('./modules/profile/pages/profile/profile.component').then((c) => c.ProfileComponent),
            },

            ...testingRoutes,
            ...spectatingOnlineGameRoutes,

            ...localTournamentPublicRoutes,
        ],
    },

    // Main paths
    {
        path: '',
        canActivate: [authGuard, notVenueModeGuard],
        canActivateChild: [notVenueModeGuard, activeLobbyGuard],
        loadComponent: () => import('./core/shell/layouts/shell/shell.component').then((c) => c.ShellComponent),
        children: [
            {
                path: DC_ROUTING.DASHBOARD,
                loadComponent: () =>
                    import('./modules/dashboard/pages/dashboard/dashboard.component').then((c) => c.DashboardComponent),
            },
            {
                path: DC_ROUTING.SETTINGS,
                loadComponent: () =>
                    import('./modules/account-settings/pages/settings-menu/settings-menu.component').then(
                        (c) => c.SettingsMenuComponentComponent
                    ),
            },
            {
                path: DC_ROUTING.APP_SETTINGS,
                loadComponent: () =>
                    import('./modules/account-settings/pages/app-settings/app-settings.component').then(
                        (c) => c.AppSettingsComponent
                    ),
            },
            {
                path: DC_ROUTING.SUBSCRIPTIONS,
                loadComponent: () =>
                    import('./modules/subscriptions/pages/subscriptions/subscriptions.component').then(
                        (c) => c.SubscriptionsComponent
                    ),
            },
            {
                path: DC_ROUTING.GAME_SETTINGS,
                loadComponent: () =>
                    import('./modules/account-settings/pages/game-settings/game-settings.component').then(
                        (c) => c.GameSettingsComponent
                    ),
            },
            {
                path: DC_ROUTING.SMART_DEVICE_SETTINGS,
                loadComponent: () =>
                    import('./modules/smart-devices-settings/pages/smart-devices-settings/smart-devices-settings.component').then(
                        (c) => c.SmartDevicesSettingsComponent
                    ),
            },
            {
                path: DC_ROUTING.PROFILE,
                data: {
                    hideTournamentNotification: true,
                },
                loadComponent: () =>
                    import('./modules/profile/pages/profile/profile.component').then((c) => c.ProfileComponent),
            },
            {
                path: 'profile/:id',
                data: {
                    hideTournamentNotification: true,
                },
                loadComponent: () =>
                    import('./modules/profile/pages/profile/profile.component').then((c) => c.ProfileComponent),
            },
            ...profileRoutes,
            {
                path: DC_ROUTING.SEARCH_USERS,
                loadComponent: () =>
                    import('./modules/search-users/pages/search-users/search-users.component').then(
                        (c) => c.SearchUsersComponent
                    ),
            },
            {
                path: DC_ROUTING.FRIENDS,
                loadComponent: () =>
                    import('./modules/my-friends/pages/my-friends/my-friends.component').then(
                        (c) => c.MyFriendsComponent
                    ),
            },
            {
                path: DC_ROUTING.ACCOUNT_SETTINGS,
                data: {
                    hideTournamentNotification: true,
                },
                loadComponent: () =>
                    import('./modules/account-settings/pages/account-settings/account-settings.component').then(
                        (c) => c.AccountSettingsComponent
                    ),
            },
            {
                path: DC_ROUTING.BLOCKED_PLAYERS,
                loadComponent: () =>
                    import('./modules/account-settings/pages/blocked-players/blocked-players.component').then(
                        (c) => c.BlockedPlayersComponent
                    ),
            },
            {
                path: DC_ROUTING.RESET_ACCOUNT,
                loadComponent: () =>
                    import('./modules/account-settings/pages/reset-account/reset-account.component').then(
                        (c) => c.ResetAccountComponent
                    ),
            },

            // Game paths
            {
                path: DC_ROUTING.LOCAL_GAMES,
                loadComponent: () =>
                    import('./modules/local-games/pages/app-local-games/app-local-games.component').then(
                        (c) => c.AppLocalGamesComponent
                    ),
            },

            // //Statistic paths
            ...statisticsRoutes,

            {
                path: DC_ROUTING.PRE_GAME,
                data: {
                    hideTournamentNotification: true,
                },
                loadComponent: () =>
                    import('./modules/games/pages/pre-game/pre-game.component').then((c) => c.PreGameComponent),
            },
            ...localGameRoutes,
            ...gameRoutes,
            ...tournamentRoutes,
            // Other
            ...actionReplaysRoutes,
            ...smartDeviceRoutes,
            ...chatRoutes,

            // Online paths
            {
                path: '',
                loadComponent: () =>
                    import('../app/modules/online-play/components/global-lobby-wrapper/online-play-global-lobby-wrapper.component').then(
                        (c) => c.OnlinePlayGlobalLobbyWrapperComponent
                    ),
                children: [...onlineGamesRoutes, ...rankingRoutes],
            },
        ],
    },
    {
        path: DC_ROUTING.VENUES,
        canActivate: [authGuard, venueModeGuard],
        loadComponent: () =>
            import('./core/shell/layouts/venue-shell/venue-shell.component').then((c) => c.VenueShellComponent),
        children: [...venuesRoutes],
    },

    {
        path: DC_ROUTING.CONNECT,
        canActivate: [connectGuard, noRedirectAuthGuard],
        loadComponent: () =>
            import('./modules/connect/pages/connect/connect.component').then((c) => c.ConnectComponent),
    },

    {
        path: 'banned',
        canActivate: [notBannedGuard],
        loadComponent: () => import('./modules/banned/pages/banned/banned.component').then((c) => c.BannedComponent),
    },

    {
        path: 'service-unavailable',
        canActivate: [serviceUnavailableGuard],
        loadComponent: () =>
            import('./modules/maintenance/pages/service-unavailable/service-unavailable.component').then(
                (c) => c.ServiceUnavailableComponent
            ),
    },

    {
        path: 'dev',
        canActivate: [developmentGuard, authGuard],
        loadComponent: () =>
            import('./modules/development/pages/dev/dev.component').then((c) => c.DevelopmentComponent),
    },

    { path: '**', redirectTo: '/dashboard' },
];

export const routes: Routes = appRoutes.map((route) =>
    route.redirectTo !== undefined
        ? route
        : {
              ...route,
              canMatch: [appMaintenanceGuard, ...(route.canMatch ?? [])],
          }
);
